import { PrivateLeaseQuestion } from '@hb/domain/steps/financial-obligation';
import { FinancialObligationsStoreService } from '@hb/modules/financial-obligations/financial-obligations-store.service';
import { FieldSubject } from '@hb/domain/field-subject';
import { ApplicantPrivateLeaseBuilder, PrivateLeaseBuilder, money } from '@hb/builder';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription, filter } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { PrivateLease } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class PrivateLeaseStoreService extends AbstractStoreInjectOption<PrivateLeaseQuestion> implements OnDestroy {
    protected readonly store = inject(FinancialObligationsStoreService);
    protected readonly lastQuestion = PrivateLeaseQuestion.SUMMARY;

    private privateLeaseSubscription: Subscription;

    private readonly mainApplicantMonthlyPaymentSubject: FieldSubject<Money>;
    private readonly coApplicantMonthlyPaymentSubject: FieldSubject<Money>;
    private readonly mainApplicantDurationInMonthsSubject: FieldSubject<number>;
    private readonly coApplicantDurationInMonthsSubject: FieldSubject<number>;

    constructor() {
        super();
        this.init();

        const initialValues = this.getInitialValues();
        this.mainApplicantMonthlyPaymentSubject = new FieldSubject<Money>(initialValues?.main_applicant?.monthly_amount);
        this.coApplicantMonthlyPaymentSubject = new FieldSubject<Money>(initialValues?.co_applicant?.monthly_amount);

        this.mainApplicantDurationInMonthsSubject = new FieldSubject<number>(initialValues?.main_applicant?.duration_in_months);
        this.coApplicantDurationInMonthsSubject = new FieldSubject<number>(initialValues?.co_applicant?.duration_in_months);
        this.subscriptions();
    }
    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.privateLeaseSubscription.unsubscribe();
        this.activeQuestionSubject.unsubscribe();
        this.mainApplicantMonthlyPaymentSubject.unsubscribe();
        this.coApplicantMonthlyPaymentSubject.unsubscribe();
        this.mainApplicantDurationInMonthsSubject.unsubscribe();
        this.coApplicantDurationInMonthsSubject.unsubscribe();
    }

    update(): void {
        const builder = new PrivateLeaseBuilder();
        if (this.shouldSetMainApplicant()) {
            builder.withMainApplicant(
                new ApplicantPrivateLeaseBuilder()
                    .withMonthlyAmount(this.mainApplicantMonthlyPaymentSubject.value)
                    .withDurationInMonths(this.mainApplicantDurationInMonthsSubject.value)
                    .build()
            );
        }
        if (this.shouldSetCoApplicant()) {
            builder.withCoApplicant(
                new ApplicantPrivateLeaseBuilder()
                    .withMonthlyAmount(this.coApplicantMonthlyPaymentSubject.value)
                    .withDurationInMonths(this.coApplicantDurationInMonthsSubject.value)
                    .build()
            );
        }
        this.store.setPrivateLease(builder.build());
    }

    private shouldSetMainApplicant() {
        return this.mainApplicantMonthlyPaymentSubject.value && this.mainApplicantDurationInMonthsSubject.value;
    }

    private shouldSetCoApplicant() {
        return this.hasCoApplicant && this.coApplicantMonthlyPaymentSubject.value && this.coApplicantDurationInMonthsSubject.value;
    }

    setMainApplicantMonthlyPayment(amount: string): void {
        this.mainApplicantMonthlyPaymentSubject.next(money(amount));
    }

    setCoApplicantMonthlyPayment(amount: string): void {
        this.coApplicantMonthlyPaymentSubject.next(money(amount));
    }

    getMainApplicantMonthlyPayment(): Money {
        return this.mainApplicantMonthlyPaymentSubject.value;
    }

    getCoApplicantMonthlyPayment(): Money {
        return this.coApplicantMonthlyPaymentSubject.value;
    }

    setMainApplicantDurationInMonths(months: number): void {
        this.mainApplicantDurationInMonthsSubject.next(months);
    }

    setCoApplicantDurationInMonths(months: number): void {
        this.coApplicantDurationInMonthsSubject.next(months);
    }

    getMainApplicantDurationInMonths(): number {
        return this.mainApplicantDurationInMonthsSubject.value;
    }

    getCoApplicantDurationInMonths(): number {
        return this.coApplicantDurationInMonthsSubject.value;
    }

    getInitialValues(): PrivateLease {
        return this.store.getFinancialObligationState()?.private_lease;
    }

    private subscriptions(): void {
        this.subscribeHasCoApplicant();
        this.subscribePrivateLeaseOption();
        this.subscribeFieldSubjects();
    }

    private subscribeHasCoApplicant(): void {
        this.getHasCoApplicant().subscribe((hasCoApplicant: boolean) => {
            if (!hasCoApplicant) {
                this.coApplicantMonthlyPaymentSubject.next(null);
                this.coApplicantDurationInMonthsSubject.next(null);
                this.update();
            }
        });
    }

    private subscribePrivateLeaseOption(): void {
        this.privateLeaseSubscription = this.store.getFinancialObligationsOptions().subscribe((options) => {
            if (!options.privateLease) {
                this.mainApplicantMonthlyPaymentSubject.next(null);
                this.coApplicantMonthlyPaymentSubject.next(null);
                this.mainApplicantDurationInMonthsSubject.next(null);
                this.coApplicantDurationInMonthsSubject.next(null);
            }
        });
    }

    private subscribeFieldSubjects(): void {
        this.mainApplicantMonthlyPaymentSubject
            .asObservable()
            .pipe(
                filter(() => this.mainApplicantDurationInMonthsSubject.value !== null),
                filter(() => this.mainApplicantDurationInMonthsSubject.value !== undefined)
            )
            .subscribe(() => this.update());

        this.mainApplicantDurationInMonthsSubject
            .asObservable()
            .pipe(
                filter(() => this.mainApplicantMonthlyPaymentSubject.value !== null),
                filter(() => this.mainApplicantMonthlyPaymentSubject.value !== undefined)
            )
            .subscribe(() => this.update());

        this.coApplicantMonthlyPaymentSubject
            .asObservable()
            .pipe(
                filter(() => this.coApplicantDurationInMonthsSubject.value !== null),
                filter(() => this.coApplicantDurationInMonthsSubject.value !== undefined)
            )
            .subscribe(() => this.update());

        this.coApplicantDurationInMonthsSubject
            .asObservable()
            .pipe(
                filter(() => this.coApplicantMonthlyPaymentSubject.value !== null),
                filter(() => this.coApplicantMonthlyPaymentSubject.value !== undefined)
            )
            .subscribe(() => this.update());
    }
}
